import { FC, useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import EastOutlinedIcon from "@mui/icons-material/EastOutlined";
import { ToastContainer, toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CancelIcon from "@mui/icons-material/Cancel";
import { NumericFormat } from "react-number-format";
import {
  CustomizedLink,
  CustomizedText,
} from "../../../components/styled-componnets";
import Colors from "../../../utils/theme/color";
import { API_CONSTANTS, UNICODE } from "../../../constants";
import {
  CustomizedCard,
  ProjectImageModalContainer,
} from "../styled-components";
import { UnitDetailsType } from "..";
import { useGetUserDetailsHooks, useScreenSizeApplier } from "../../../hooks";
import {
  OpportunityEditableStatuses,
  Status,
} from "../../../constants/statuses";
import MaterialModal from "../../../components/modal";
import { useUpdateOpportunityMutation } from "../../../../features/api/opportunity-api-slice";
// ✅ 1. Import RTK Query hooks for projects and units
import {
  useGetProjectsQuery,
  useGetUnitListQuery,
} from "../../../../features/api/project-api-slice";

const ERROR = "error";
const customizedCardProps = { p: "1rem", pr: "3rem" };

interface ProjectAndUnitDetailsTypes {
  project?: string;
  unitDetails?: UnitDetailsType;
  opportunityId?: string;
  status?: Status;
  unitType?: string;
  onEditClick?: () => void;
  unit?: string;
}
interface ProjectDetailsType {
  smaller_display_image?: string;
  name?: string;
  location_name?: string;
  short_description?: string;
  minimum_cost?: string;
  maximum_cost?: string;
  type?: string;
}

// Define API response types for clarity (can be moved to a types file)
interface ProjectsApiResponse {
  message: { name: string }[];
}
interface UnitTypesApiResponse {
  data: { name: string; type: string }[];
}

const materialModalProps = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
};

const ProjectAndUnitDetails: FC<ProjectAndUnitDetailsTypes> = ({
  project = "",
  unitDetails = {},
  opportunityId = "",
  status = Status.AVAILABLE,
  unitType = "",
  onEditClick,
  unit = "",
}) => {
  const { t } = useTranslation();
  const { mergeStyles } = useScreenSizeApplier();
  const { getUserToken, isProjectSalesUser, isProjectSalesHead } =
    useGetUserDetailsHooks();

  const token = getUserToken();

  const [projectDetails, setProjectDetails] = useState<ProjectDetailsType>({});
  const [showProjectImageModal, setShowProjectImageModal] =
    useState<boolean>(false);
  const [isUnitDialogBoxOpen, setIsUnitDialogBoxOpen] =
    useState<boolean>(false);

  const [selectedProject, setSelectedProject] = useState("");
  const [selectedUnitType, setSelectedUnitType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // ✅ 2. Replace manual fetch and useState with RTK Query hooks
  // Hook for fetching the projects list
  const {
    data: projectsData,
    isLoading: isProjectsLoading,
    isError: isProjectsError,
  } = useGetProjectsQuery({});

  // Hook for fetching unit types based on the selected project.
  // The 'skip' option prevents this query from running until a project is selected.
  const { data: unitTypesData, isLoading: isUnitTypesLoading } =
    useGetUnitListQuery(
      { project: selectedProject, userToken: token },
      { skip: !selectedProject || !token }
    );

  const [updateOpportunity] = useUpdateOpportunityMutation();
  
  const returnProjectDetailsById = useCallback(
    (projectList: { name: string }[]) =>
      projectList.filter((item) => item?.name === project)[0],
    [project]
  );
  // ✅ 3. Removed the two useEffect hooks that contained fetch logic.
  // This useEffect remains, as it processes the data after it has been fetched by RTK Query.
  useEffect(() => {
    if (projectsData?.message?.length) {
      setProjectDetails(returnProjectDetailsById(projectsData.message));
    } else if (isProjectsError) {
      toast(t("error_messages.server_error"), {
        type: ERROR,
      });
      setProjectDetails({});
    }
  }, [projectsData, isProjectsError, t, returnProjectDetailsById]);
  


  const toggleImageModal = useCallback(
    () => setShowProjectImageModal(!showProjectImageModal),
    [showProjectImageModal]
  );

  const handleOpenUnitDialog = useCallback(() => {
    setIsUnitDialogBoxOpen(true);
  }, []);

  const handleCloseUnitDialog = useCallback(() => {
    setIsUnitDialogBoxOpen(false);
    setSelectedProject("");
    setSelectedUnitType("");
  }, []);

  const handleUnitTypeChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value as string;
    setSelectedUnitType(value);
    if (value) {
      console.log("Success! Selected Unit Type:", value);
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // STEP 1: Get current opportunity
      const getOpportunityResponse = await fetch(
        `${API_CONSTANTS.baseURL}/api/resource/Opportunity/${opportunityId}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            Authorization: `token ${token}`,
          },
        }
      );
      if (!getOpportunityResponse.ok) {
        throw new Error("Failed to fetch current opportunity details.");
      }

      const opportunityData = await getOpportunityResponse.json();
      const currentOpportunityDetails = opportunityData.data;
      if (!currentOpportunityDetails) {
        throw new Error("Received empty data for the opportunity.");
      }

      // STEP 2: Find selected unit object
      const selectedUnitObject = unitTypesData?.data?.find(
        (unit : any) => unit.name === selectedUnitType
      );

      // STEP 3: Build payload
      const payload = {
        ...currentOpportunityDetails,
        project: selectedProject,
        unit: selectedUnitType,
        unit_type: selectedUnitObject
          ? selectedUnitObject.type
          : currentOpportunityDetails.unit_type,
        name: opportunityId,
      };

      console.log(
        "Final Update Payload Being Sent:",
        JSON.stringify(payload, null, 2)
      );

      // Call RTK mutation
      const result = await updateOpportunity({
        payload,
        userToken: token,
      }).unwrap();

      console.log("Update success:", result);
      toast.success("Project and Unit have been updated successfully!");

      handleCloseUnitDialog();
      setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      console.error("Update process failed:", error);
      toast.error("An error occurred while updating. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderProjectImage = () => (
    <img
      src={`${API_CONSTANTS.baseURL}${projectDetails?.smaller_display_image}`}
      height={"100%"}
      width={"100%"}
      alt={"project"}
      style={{ borderRadius: 10, cursor: "pointer" }}
      onClick={toggleImageModal}
    />
  );

  const renderProjectAndUnitName = () =>
    projectDetails?.name && (
      <CustomizedText
        fontSize={24}
        font={"Lora-Medium"}
        fontColor={Colors.primaryColor}
      >
        {`${projectDetails?.name}${
          unitDetails?.unit
            ? `,${unitDetails?.unit}`
            : unitType
            ? `,${unitType}`
            : ""
        }`}
      </CustomizedText>
    );

  const renderUnitDetails = () => (
    <CustomizedText
      fontSize={16}
      font={"Lato-Regular"}
      sx={{ display: "flex", alignItems: "center" }}
      gutterBottom
    >
      {`${unitDetails?.bhk ? unitDetails.bhk : ""}${
        unitDetails?.block
          ? `${unitDetails.bhk && ","} ${t("opportunity_details.block", {
              number: unitDetails.block,
            })}`
          : ""
      }${
        unitDetails?.floor
          ? `${unitDetails?.bhk ?? unitDetails?.block ? "," : ""} ${t(
              "opportunity_details.floor_no",
              { number: unitDetails.floor }
            )}`
          : ""
      }`}
    </CustomizedText>
  );

  const renderUnitDetailsOrLocation = () =>
    unitDetails?.unit ? (
      renderUnitDetails()
    ) : (
      projectDetails?.location_name && (
        <CustomizedText
          fontSize={16}
          font={"Lato-Regular"}
          sx={{ display: "flex", alignItems: "center" }}
          gutterBottom
        >
          <LocationOnIcon sx={{ fontSize: 16 }} />
          {projectDetails?.location_name}
        </CustomizedText>
      )
    );

  const renderUnitArea = () => (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {unitDetails?.super_builtup_area && (
        <CustomizedText
          fontSize={16}
          font={"Lora-Medium"}
          fontColor={Colors.greyColor}
        >
          {`${t("opportunity_details.super_built_up_area", {
            area: unitDetails.super_builtup_area,
          })} `}
        </CustomizedText>
      )}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {unitDetails?.super_builtup_area ? (
          <CustomizedText fontSize={10} sx={{ mr: 0.5, ml: 0.5 }}>
            {` ${UNICODE.dot} `}
          </CustomizedText>
        ) : null}
        {unitDetails?.carpet_area && (
          <CustomizedText
            fontSize={16}
            font={"Lora-Medium"}
            fontColor={Colors.greyColor}
          >
            {t("opportunity_details.carpet_area", {
              area: unitDetails.carpet_area,
            })}
          </CustomizedText>
        )}
      </Box>
    </Box>
  );

  const renderProjectDetails = () => (
    <>
      {renderProjectAndUnitName()}
      {renderUnitDetailsOrLocation()}
      {unitDetails?.name ? (
        renderUnitArea()
      ) : (
        <CustomizedText
          fontSize={18}
          font={"Lato-Regular"}
          fontColor={Colors.greyColor}
          sx={{ mt: "0.5rem" }}
          gutterBottom
        >
          {projectDetails?.short_description}
        </CustomizedText>
      )}
    </>
  );

  const renderPriceDetailsAndProceedLink = () => (
    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
      {unitDetails?.name && unitDetails?.total_cost ? (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <CustomizedText
            fontSize={16}
            font={"Lora-Medium"}
            fontColor={Colors.greyColor}
            sx={{ mr: 1 }}
          >
            {t("opportunity_details.total_cost")}
          </CustomizedText>
          <NumericFormat
            style={{
              fontFamily: "Lora-Medium",
              fontSize: 20,
              color: Colors.titleColor,
            }}
            value={unitDetails?.total_cost}
            allowLeadingZeros
            thousandSeparator=","
            prefix={`${UNICODE.rupeeSymbol}`}
            displayType="text"
            thousandsGroupStyle="lakh"
          />
          <CustomizedText
            fontSize={16}
            font={"Lora-Medium"}
            fontColor={Colors.greyColor}
            sx={{ display: "flex", alignItems: "center", ml: 1 }}
          >
            {t("opportunity_details.subject_to_change")}
          </CustomizedText>
        </Box>
      ) : projectDetails?.minimum_cost ? (
        <CustomizedText fontSize={20} font={"Lora-Medium"}>
          {`${projectDetails?.minimum_cost}-${projectDetails?.maximum_cost}`}
        </CustomizedText>
      ) : (
        <div />
      )}
      {!unitDetails?.name &&
        status &&
        OpportunityEditableStatuses.includes(status) && (
          <CustomizedLink
            href={`/opportunity/${opportunityId}/edit`}
            fontSize={24}
            sx={{ display: "flex", alignItems: "center" }}
          >
            {t("opportunity_details.proceed_to_add_unit_details")}
            <EastOutlinedIcon sx={{ fontSize: 16, ml: 0.3 }} />
          </CustomizedLink>
        )}
      {(isProjectSalesUser || isProjectSalesHead) && unitDetails?.name && (
        <CustomizedLink
          fontSize={24}
          sx={{ display: "flex", alignItems: "center" }}
          style={{
            cursor: "pointer",
            color: "blue",
            textDecoration: "underline",
          }}
          onClick={handleOpenUnitDialog}
        >
          {t("opportunity_details.proceed_to_add_unit_details")}
        </CustomizedLink>
      )}
    </Box>
  );

  const renderImage = () => (
    <ProjectImageModalContainer>
      <img
        src={`${API_CONSTANTS.baseURL}${projectDetails?.smaller_display_image}`}
        height={"80%"}
        width={"40%"}
        alt={"project"}
        style={{ borderRadius: 10 }}
      />
      <CancelIcon
        htmlColor={Colors.white}
        sx={{ ml: 1 }}
        fontSize={"small"}
        onClick={toggleImageModal}
      />
    </ProjectImageModalContainer>
  );

  return (
    <>
      <CustomizedCard
        sx={{
          background: "#ffffff",
          borderRadius: "16px",
          border: "1.5px solid #e5e7eb",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.03)",
          p: 3,
          mb: 2,
          mt: 2,
          width: "95%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <CustomizedText
              fontSize={12}
              fontColor="#9ca3af"
              font="Lato-Bold"
              sx={{ textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}
            >
              {t("opportunity_details.interested_project_and_unit")}
            </CustomizedText>
            
            <CustomizedText
              fontSize={20}
              font="Lora-Bold"
              fontColor="#15803d"
            >
              {`${projectDetails?.name || project}${
                unitDetails?.unit
                  ? `, ${unitDetails?.unit}`
                  : unitType
                  ? `, ${unitType}`
                  : ""
              }`}
            </CustomizedText>

            {projectDetails?.location_name && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
                <LocationOnIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                <CustomizedText
                  fontSize={14}
                  font="Lato-Regular"
                  fontColor="#6b7280"
                >
                  {projectDetails?.location_name}
                </CustomizedText>
              </Box>
            )}

            {unitDetails?.name && (unitDetails?.super_builtup_area || unitDetails?.carpet_area) && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                {unitDetails?.super_builtup_area && (
                  <CustomizedText fontSize={14} font="Lora-Medium" fontColor="#6b7280">
                    {t("opportunity_details.super_built_up_area", { area: unitDetails.super_builtup_area })}
                  </CustomizedText>
                )}
                {unitDetails?.super_builtup_area && unitDetails?.carpet_area && (
                  <CustomizedText fontSize={10} fontColor="#9ca3af">•</CustomizedText>
                )}
                {unitDetails?.carpet_area && (
                  <CustomizedText fontSize={14} font="Lora-Medium" fontColor="#6b7280">
                    {t("opportunity_details.carpet_area", { area: unitDetails.carpet_area })}
                  </CustomizedText>
                )}
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>
            {!unit && status && OpportunityEditableStatuses.includes(status) && !(isProjectSalesUser || isProjectSalesHead) && (
              <Button
                variant="outlined"
                href={`/opportunity/${opportunityId}/edit`}
                sx={{
                  borderColor: "#16a34a",
                  color: "#16a34a",
                  borderRadius: "8px",
                  padding: "8px 16px",
                  textTransform: "none",
                  fontFamily: "Lora-Medium",
                  fontWeight: "600",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  "&:hover": {
                    borderColor: "#15803d",
                    background: "#f0fdf4",
                  },
                }}
              >
                {t("opportunity_details.proceed_to_add_unit_details")}
                <EastOutlinedIcon sx={{ fontSize: 16 }} />
              </Button>
            )}
            {(isProjectSalesUser || isProjectSalesHead) && !unit && (
              <Button
                variant="outlined"
                onClick={handleOpenUnitDialog}
                sx={{
                  borderColor: "#16a34a",
                  color: "#16a34a",
                  borderRadius: "8px",
                  padding: "8px 16px",
                  textTransform: "none",
                  fontFamily: "Lora-Medium",
                  fontWeight: "600",
                  fontSize: "14px",
                  "&:hover": {
                    borderColor: "#15803d",
                    background: "#f0fdf4",
                  },
                }}
              >
                {t("opportunity_details.proceed_to_add_unit_details")}
              </Button>
            )}

            {unitDetails?.name && unitDetails?.total_cost ? (
              <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                <CustomizedText fontSize={14} font="Lora-Medium" fontColor="#6b7280" sx={{ mr: 1 }}>
                  {t("opportunity_details.total_cost")}
                </CustomizedText>
                <NumericFormat
                  style={{
                    fontFamily: "Lora-Medium",
                    fontSize: 18,
                    color: "#111827",
                    fontWeight: 700,
                  }}
                  value={unitDetails?.total_cost}
                  allowLeadingZeros
                  thousandSeparator=","
                  prefix={`${UNICODE.rupeeSymbol}`}
                  displayType="text"
                  thousandsGroupStyle="lakh"
                />
              </Box>
            ) : projectDetails?.minimum_cost ? (
              <CustomizedText fontSize={18} font="Lora-Bold" fontColor="#111827" sx={{ mt: 1 }}>
                {`${UNICODE.rupeeSymbol} ${projectDetails?.minimum_cost} - ${projectDetails?.maximum_cost}`}
              </CustomizedText>
            ) : null}
          </Box>
        </Box>
      </CustomizedCard>
      <MaterialModal
        children={renderImage()}
        sx={materialModalProps}
        open={showProjectImageModal}
        onClose={toggleImageModal}
      />
      <Dialog
        open={isUnitDialogBoxOpen}
        onClose={handleCloseUnitDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Project and Unit Preferences</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 2,
              pt: 2,
            }}
          >
            {/* Project Selector */}
            <FormControl fullWidth required>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Project <span style={{ color: "red" }}>*</span>
              </Typography>
              <Select
                value={selectedProject}
                displayEmpty
                onChange={(e) => {
                  setSelectedProject(e.target.value);
                  setSelectedUnitType(""); // Reset unit type on project change
                }}
                renderValue={(selected) =>
                  selected ? selected : "Choose a project"
                }
                MenuProps={{
                  disablePortal: true,
                  PaperProps: { style: { maxHeight: 300 } },
                }}
                disabled={isProjectsLoading}
              >
                {isProjectsLoading && (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <em>Loading projects...</em>
                  </MenuItem>
                )}
                {projectsData?.message?.map((project: { name: string }) => (
                  <MenuItem key={project.name} value={project.name}>
                    {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Unit Selector */}
            <FormControl
              fullWidth
              required
              disabled={!selectedProject || isUnitTypesLoading}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                Unit <span style={{ color: "red" }}>*</span>
              </Typography>
              <Select
                value={selectedUnitType}
                displayEmpty
                onChange={handleUnitTypeChange}
                renderValue={(selected) =>
                  selected ? selected : "Select the unit"
                }
                MenuProps={{
                  disablePortal: true,
                  PaperProps: { style: { maxHeight: 300 } },
                }}
              >
                {isUnitTypesLoading && (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <em>Loading unit types...</em>
                  </MenuItem>
                )}
                {unitTypesData?.data?.map(
                  (unit: { name: string; type: string }) => (
                    <MenuItem key={unit.name} value={unit.name}>
                      {unit.name}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", padding:"0 25px 10px" }}>
          <Button onClick={handleCloseUnitDialog} sx={{ color: "teal" }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!selectedUnitType || isSubmitting}
            sx={{
              bgcolor: "#e0e0e0",
              color: "#555",
              "&:disabled": { bgcolor: "#f5f5f5", color: "#aaa" },
            }}
          >
            {isSubmitting ? <CircularProgress size={24} /> : "SAVE"}
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer />
    </>
  );
};

export default ProjectAndUnitDetails;